import {
  GamoraBase64URLGuard,
  GamoraErrorUnhandledException,
  GamoraFoundationGuard,
  GamoraUUIDGuard,
  assert,
} from '@ver.id/libs.internal.gamora';

/**
 * Input type
 * @public
 */
export interface HoruzProtocolChulakServerSessionGuardInput {
  uuid: string;
  serviceSessionUuidsByServiceName: [string, string[]][];
}

/**
 * @public
 */
export class HoruzProtocolChulakServerSessionGuard extends GamoraFoundationGuard<HoruzProtocolChulakServerSessionGuardInput> {
  protected _uuid: GamoraUUIDGuard = GamoraUUIDGuard.Empty();
  protected _serviceSessionUuidsByServiceName: Map<string, GamoraUUIDGuard[]> =
    new Map();

  /**
   * Constructor
   * @param input - the input
   */
  constructor(input: HoruzProtocolChulakServerSessionGuardInput) {
    super();

    // Identifier
    this._uuid = GamoraUUIDGuard.fromJSON(input.uuid);

    // Add service sessions
    this.serviceSessionUuidsByServiceName =
      input.serviceSessionUuidsByServiceName.map(
        ([serviceName, serviceSessionUuids]) => {
          assert(
            Array.isArray(serviceSessionUuids),
            'Invalid serviceSessionUuids: not a valid array.',
          );
          return [
            serviceName,
            serviceSessionUuids.map((serviceSessionUuid) =>
              GamoraUUIDGuard.fromJSON(serviceSessionUuid),
            ),
          ];
        },
      );
  }

  /**
   * Get
   */
  get uuid() {
    return this._uuid.clone();
  }
  set uuid(uuid: GamoraUUIDGuard) {
    assert(
      uuid instanceof GamoraUUIDGuard,
      'Invalid uuid: not a valid GamoraUUIDGuard.',
    );
    this._uuid = uuid.clone();
  }

  /**
   * Get
   */
  get serviceSessionUuidsByServiceName() {
    return Array.from(this._serviceSessionUuidsByServiceName.entries()).map(
      ([serviceName, serviceSessionUuids]) => [
        serviceName,
        serviceSessionUuids.map((serviceSessionUuid) =>
          serviceSessionUuid.clone(),
        ),
      ],
    );
  }
  set serviceSessionUuidsByServiceName(
    serviceSessionEntries: [string, GamoraUUIDGuard[]][],
  ) {
    assert(
      Array.isArray(serviceSessionEntries),
      'Invalid serviceSessionMap: not a valid array.',
    );
    assert(
      serviceSessionEntries.every(([serviceName, serviceSessionUuids]) => {
        return (
          typeof serviceName === 'string' &&
          serviceName.length > 0 &&
          Array.isArray(serviceSessionUuids) &&
          serviceSessionUuids.every(
            (serviceSessionUuid) =>
              serviceSessionUuid instanceof GamoraUUIDGuard,
          )
        );
      }),
      'Invalid serviceSessionUuidsByServiceName: not a valid GamoraUUIDGuard array.',
    );

    this._serviceSessionUuidsByServiceName = new Map();

    // Set map
    for (const [serviceName, serverSessionUuids] of serviceSessionEntries) {
      this._serviceSessionUuidsByServiceName.set(
        serviceName,
        serverSessionUuids.map((serviceSessionUuid) =>
          serviceSessionUuid.clone(),
        ),
      );
    }
  }

  /**
   * Creates emtpy
   * @returns
   */
  static override Empty(): HoruzProtocolChulakServerSessionGuard {
    return HoruzProtocolChulakServerSessionGuard.fromJSON({
      uuid: GamoraUUIDGuard.Empty().toJSON(),
      serviceSessionUuidsByServiceName: [],
    } as HoruzProtocolChulakServerSessionGuardInput);
  }

  /**
   * Gets from JSON
   * @param input - Input string
   */
  static override fromJSON(
    input: unknown,
  ): HoruzProtocolChulakServerSessionGuard {
    assert(
      typeof input === 'object' && input !== null,
      'Invalid input argument: not an object.',
    );
    return new HoruzProtocolChulakServerSessionGuard(
      input as HoruzProtocolChulakServerSessionGuardInput,
    );
  }

  /**
   * Get from base64URL
   * @param input - The input
   */
  static override fromBase64URL(
    input: unknown,
  ): HoruzProtocolChulakServerSessionGuard {
    assert(
      typeof input === 'string' && input.length > 0,
      'Invalid input argument: not a valid string.',
    );

    // Parse input
    let json;
    try {
      json = JSON.parse(GamoraBase64URLGuard.fromBase64URL(input).toUTF8());
    } catch {
      throw new GamoraErrorUnhandledException(
        'Unable to parse input: not valid JSON in base64url input.',
      );
    }

    // Create new guard
    return new HoruzProtocolChulakServerSessionGuard(
      json as HoruzProtocolChulakServerSessionGuardInput,
    );
  }

  /**
   * Gets JSON
   * @returns string
   */
  override toJSON(): HoruzProtocolChulakServerSessionGuardInput {
    return {
      uuid: this.uuid.toJSON(),
      serviceSessionUuidsByServiceName:
        this.serviceSessionUuidsByServiceName.map(
          ([serviceName, serviceSessionUuids]) => [
            serviceName,
            serviceSessionUuids.map((serviceSessionUuid) =>
              serviceSessionUuid.toJSON(),
            ),
          ],
        ),
    };
  }

  /**
   * To Base64URL
   */
  override toBase64URL(): string {
    const json = this.toJSON();
    return GamoraBase64URLGuard.fromUTF8(JSON.stringify(json)).toBase64URL();
  }

  /**
   * Gets mutable service session uuids
   */
  getServiceSessionUuidsByServiceNameMap() {
    return this._serviceSessionUuidsByServiceName;
  }

  /**
   * Has an active service session uuid by service name
   * @param serviceName The service name
   */
  hasActiveServiceSessionUuidByServiceName(serviceName: string): boolean {
    if (this._serviceSessionUuidsByServiceName.has(serviceName)) {
      const serviceSessionUuids =
        this._serviceSessionUuidsByServiceName.get(serviceName)!;
      return serviceSessionUuids.length !== 0;
    }
    return false;
  }

  /**
   * Get the active service session uuid by service name
   * @param serviceName The service name
   */
  getActiveServiceSessionUuidByServiceName(serviceName: string) {
    assert(
      this._serviceSessionUuidsByServiceName.has(serviceName),
      'Invalid serviceName: the service name does not exist.',
    );
    const serviceSessionUuids =
      this._serviceSessionUuidsByServiceName.get(serviceName)!;
    assert(
      serviceSessionUuids.length !== 0,
      'No active service session: service session list is empty',
    );
    return serviceSessionUuids[serviceSessionUuids.length - 1];
  }

  /**
   * Adds service session uuid to map by service name
   * @param serviceName The service name
   * @param serviceSessionUuid The service session uuid
   */
  addServiceSessionUuidByServiceName(
    serviceName: string,
    serviceSessionUuid: GamoraUUIDGuard,
  ) {
    // Check if name exists
    if (!this._serviceSessionUuidsByServiceName.has(serviceName)) {
      this._serviceSessionUuidsByServiceName.set(serviceName, []);
    }

    // Add to map
    this._serviceSessionUuidsByServiceName
      .get(serviceName)!
      .push(serviceSessionUuid.clone());
  }

  /**
   * Removes service session uuid to map by service name
   * @param serviceName The service name
   * @param serviceSessionUuid The service session uuid
   */
  removeServiceSessionUuidByServiceName(
    serviceName: string,
    serviceSessionUuid: GamoraUUIDGuard,
  ) {
    assert(
      this._serviceSessionUuidsByServiceName.has(serviceName),
      'Invalid serviceName argument: not found in map.',
    );
    const serviceSessionUuids =
      this._serviceSessionUuidsByServiceName.get(serviceName)!;

    // Find service session uuid
    const serviceSessionUuidIdx = serviceSessionUuids.findIndex(
      (serviceSessionUuidToMatch) => {
        return (
          serviceSessionUuidToMatch.toJSON() === serviceSessionUuid.toJSON()
        );
      },
    );
    assert(
      serviceSessionUuidIdx !== -1,
      'Invalid serviceSessionUuid: not found in map.',
    );

    // Remove from map
    serviceSessionUuids.splice(serviceSessionUuidIdx, 1);
  }

  /**
   * Create clone
   */
  override clone() {
    return HoruzProtocolChulakServerSessionGuard.fromJSON(this.toJSON());
  }
}
